/* eslint-disable no-unused-vars */
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { styled } from '@mui/material/styles';

const MarkdownContainer = styled('div')({
    fontFamily: 'Rubik, sans-serif',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '0.875rem !important', //14px
    lineHeight: '16px',
    letterSpacing: '0.25px',
    wordBreak: 'break-word',
    '& h1, & h2, & h3, & h4, & h5, & h6, & p, & ul, & ol, & pre, & blockquote': {
        margin: '0 0 8px 0',
        '&:last-child': {
            marginBottom: 0,
        },
    },
    '& ul, & ol': {
        paddingLeft: '20px',
    },
    '& blockquote': {
        borderLeft: '4px solid #1F4C5C',
        paddingLeft: '16px',
        color: 'var(--text-secondary)',
    },
    '& code': {
        backgroundColor: 'var(--divider)',
        padding: '2px 4px',
        borderRadius: '8px',
        fontFamily: 'monospace',
    },
    '& pre': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
        padding: '16px',
        borderRadius: '8px',
        overflowX: 'auto',
        '& code': {
            backgroundColor: 'transparent',
            padding: 0,
        },
    },
    '& a': {
        color: 'var(--text-primary)',
    },
});

export const isMarkdownContent = content => {
    if (!content || typeof content !== 'string') return false;

    const markdownPatterns = [
        /(?:^|\n)#{1,6}\s+.+/m, // Headers (at start of line)
        /\*\*(?!\s).+?(?<!\s)\*\*/m, // Bold (match **text**)
        /\*(?!\s).+?(?<!\s)\*/m, // Italic (match *text*, excluding surrounding spaces)
        /\[[^\]]+]\([^)]+\)/m, // Links [text](url)
        /(?:^|\n)```[\S\s]*?```/m, // Code blocks (```)
        /(?:^|\n)~~~[\S\s]*?~~~/m, // Alternative code blocks
        /(?:^|\n)\s*[*+-]\s+.+/m, // Unordered lists
        /(?:^|\n)\s*\d+\.\s+.+/m, // Ordered lists
        /~~(?!\s)[^~]+(?<!\s)~~/m, // Strikethrough
        /(?:^|\n)\s*>\s+.+/m, // Blockquotes
        /(?:^|\n)\s*(?:-{3,}|\*{3,}|_{3,})\s*(?:\n|$)/m, // Horizontal rules
        /`(?!\s)[^`]+(?<!\s)`/m, // Inline code
        /(?:^|\n)\|(?:.+\|)+\s*(?:\n\|(?:[:-]+\|)+)?/m, // Tables
        /!\[[^\]]*]\([^)]+\)/m, // Images
    ];

    return markdownPatterns.some(pattern => pattern.test(content));
};

const sanitizeSchema = {
    ...defaultSchema,
    protocols: {
        ...defaultSchema.protocols,
        href: [...(defaultSchema.protocols.href || []), 'tel'],
    },
};

const MarkdownRenderer = ({ content, className }) => {
    if (!content) return null;

    const transformUrl = (url, key) => {
        if (key === 'href' && /^\+?\d[\d\s-]*\d$/.test(url)) {
            return `tel:${url.replace(/\s|-/g, '')}`;
        }
        return url;
    };

    return (
        <MarkdownContainer className={className}>
            <ReactMarkdown
                rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
                urlTransform={transformUrl}
                components={{
                    h1: ({ node, ...props }) => <h1 style={{ fontSize: '0.875rem', fontWeight: 500 }} {...props} />,
                    h2: ({ node, ...props }) => <h2 style={{ fontSize: '0.875rem', fontWeight: 500 }} {...props} />,
                    h3: ({ node, ...props }) => <h3 style={{ fontSize: '0.875rem', fontWeight: 500 }} {...props} />,
                    h4: ({ node, ...props }) => <h4 style={{ fontSize: '0.875rem', fontWeight: 500 }} {...props} />,
                    strong: ({ node, ...props }) => <strong style={{ fontWeight: 500 }} {...props} />,
                    a: ({ node, href, ...props }) => {
                        const isPhoneNumber = href?.startsWith('tel:');
                        return (
                            <a
                                href={href}
                                target={isPhoneNumber ? '_self' : '_blank'}
                                rel="noopener noreferrer"
                                {...props}
                            />
                        );
                    },
                }}
            >
                {content}
            </ReactMarkdown>
        </MarkdownContainer>
    );
};

export default MarkdownRenderer;
